import { Link } from "react-router"
import { Facebook, Instagram, MapPin, Phone, Mail, Clock } from "lucide-react"

// ─── TikTok icon (lucide chưa có) ────────────────────────────────
function TiktokIcon({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
            <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.59 2.59 0 1 1 .76-5.06V9.7a5.66 5.66 0 0 0-.76-.05 5.68 5.68 0 1 0 5.68 5.68V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3a4.28 4.28 0 0 1-3.24-1.48z" />
        </svg>
    )
}

const NAV_LINKS = [
    { label: "Trang chủ", to: "/" },
    { label: "Phòng nghỉ", to: "/phong-nghi" },
    { label: "Đặt phòng", to: "/dat-phong" },
    { label: "Thư viện", to: "/thu-vien-anh" },
]

const POLICY_LINKS = [
    "Chính sách đặt phòng",
    "Chính sách hủy phòng",
    "Chính sách bảo mật",
    "Điều khoản sử dụng",
    "Quy chế hoạt động",
    "Chính sách thanh toán",
]

const SOCIALS = [
    { icon: Facebook, label: "Facebook", href: "#" },
    { icon: Instagram, label: "Instagram", href: "#" },
    { icon: TiktokIcon, label: "TikTok", href: "#" },
]

function ColumnHeading({ children }: { children: React.ReactNode }) {
    return (
        <div className="mb-5">
            <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-white">{children}</h2>
            <span className="mt-2 block h-0.5 w-8 rounded-full bg-[#c9a86a]" />
        </div>
    )
}

/**
 * Chân trang — thương hiệu, liên kết, chính sách, liên hệ
 */
export const Footer = () => {
    return (
        <footer className="bg-[#1c1c26] text-[#a0a0ab]">
            <div className="mx-auto max-w-[1200px] px-6 py-14 lg:px-8">
                <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
                    {/* Cột thương hiệu */}
                    <div>
                        <h1 className="font-display text-2xl font-bold tracking-[0.12em] text-white">
                            NHÀ CAM
                        </h1>
                        <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#c9a86a]">
                            Stay · Chill · Repeat
                        </p>
                        <p className="mt-4 max-w-xs text-sm leading-relaxed text-[#8f8f9b]">
                            Không gian nghỉ dưỡng tại Vĩnh Long, mang đến trải nghiệm thư giãn tuyệt vời cho quý khách.
                        </p>
                        <div className="mt-5 flex gap-3">
                            {SOCIALS.map(({ icon: Icon, label, href }) => (
                                <a
                                    key={label}
                                    href={href}
                                    aria-label={label}
                                    className="flex size-9 items-center justify-center rounded-md border border-white/10 bg-white/5 text-[#b6b6c0] transition-colors hover:border-[#c9a86a]/60 hover:bg-white/10 hover:text-white"
                                >
                                    <Icon className="size-4" />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Liên kết */}
                    <div>
                        <ColumnHeading>Liên kết</ColumnHeading>
                        <ul className="space-y-3">
                            {NAV_LINKS.map((link) => (
                                <li key={link.to}>
                                    <Link
                                        to={link.to}
                                        className="group inline-flex items-center gap-2 text-sm text-[#9a9aa5] transition-colors hover:text-white"
                                    >
                                        <span className="text-[#c9a86a]">—</span>
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Chính sách */}
                    <div>
                        <ColumnHeading>Chính sách</ColumnHeading>
                        <ul className="space-y-3">
                            {POLICY_LINKS.map((label) => (
                                <li key={label}>
                                    <Link
                                        to="/chinh-sach"
                                        className="inline-flex items-center gap-2 text-sm text-[#9a9aa5] transition-colors hover:text-white"
                                    >
                                        <span className="text-[#c9a86a]">—</span>
                                        {label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Liên hệ */}
                    <div>
                        <ColumnHeading>Liên hệ</ColumnHeading>
                        <ul className="space-y-4 text-sm text-[#9a9aa5]">
                            <li className="flex items-start gap-3">
                                <MapPin className="mt-0.5 size-4 shrink-0 text-[#c9a86a]" />
                                <span>68 Lò Rèn phường 4, Vĩnh Long, Vietnam</span>
                            </li>
                            <li className="flex items-center gap-3">
                                <Phone className="size-4 shrink-0 text-[#c9a86a]" />
                                <a href="tel:0968185365" className="transition-colors hover:text-white">0968 185 365</a>
                            </li>
                            <li className="flex items-center gap-3">
                                <Mail className="size-4 shrink-0 text-[#c9a86a]" />
                                <a href="mailto:nhacamhouse@gmail.com" className="transition-colors hover:text-white">
                                    nhacamhouse@gmail.com
                                </a>
                            </li>
                            <li className="flex items-center gap-3">
                                <Clock className="size-4 shrink-0 text-[#c9a86a]" />
                                <span>Hàng ngày: 07:00 - 22:00</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Thanh bản quyền */}
            <div className="border-t border-white/10">
                <div className="mx-auto flex max-w-[1200px] flex-col items-center gap-3 px-6 py-5 text-xs text-[#6f6f7a] md:flex-row md:justify-between lg:px-8">
                    <p className="text-center md:text-left">
                        © 2026 NHÀ CAM. All rights reserved.
                        <span className="mx-2 text-white/20">|</span>
                        Website thiết kế bởi <span className="font-semibold text-[#c9a86a]">TrioCore Solutions</span>
                    </p>
                    <div className="flex items-center gap-5">
                        <Link to="/chinh-sach" className="transition-colors hover:text-white">Chính sách bảo mật</Link>
                        <Link to="/chinh-sach" className="transition-colors hover:text-white">Điều khoản</Link>
                        <Link to="/chinh-sach" className="transition-colors hover:text-white">Quy chế</Link>
                    </div>
                </div>
            </div>
        </footer>
    )
}
